import { useEffect, useRef, useState } from "react";
import { getBinding, getScreenId, setBinding } from "../lib/binding";
import { formatBootTrace, noteBoot } from "../lib/bootTrace";
import { bridge, deviceModel } from "../lib/foyerBridge";
import { hostParams } from "../lib/hostQuery";
import { isTranscludedHost, restartApp } from "../lib/hostReload";
import "../styles.css";

interface PreviewResponse {
  premise: { id: string; name: string };
  rooms: Array<{ id: string; name?: string; taken: boolean }>;
  expiresAt: string;
}

type Stage = "code" | "room";

const CODE_LEN = 6;

/**
 * Build version baked at compile time (set via VITE_APP_VERSION). Falls
 * back to "dev" in local development so the bottom meta strip always
 * has something to show.
 */
const APP_VERSION =
  (import.meta.env?.VITE_APP_VERSION as string | undefined) ?? "dev";

function arrowRightIcon() {
  return (
    <svg viewBox="0 0 14 14">
      <path d="M3 7h8M7.5 3.5L11 7l-3.5 3.5" />
    </svg>
  );
}

function backspaceIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 6l-6 6 6 6h11a1 1 0 001-1V7a1 1 0 00-1-1H9z" />
      <path d="M13 10l4 4M17 10l-4 4" />
    </svg>
  );
}

/** How many times to retry a device-model read that came back empty, and
 *  how long to wait between attempts. See {@link deviceModelWithRetry}. */
const MODEL_RETRY_ATTEMPTS = 5;
const MODEL_RETRY_DELAY_MS = 400;

/**
 * `deviceModel()`'s underlying native/Tizen read (`bridge.getDeviceInfo()`)
 * is a one-shot synchronous call, and it can genuinely fail immediately after
 * a reload rather than only on hardware that has no privileged context at
 * all. On Samsung, `webapis.productinfo` only answers inside the widget's
 * transcluded, privileged context (`tizen/index.html`) - a genuine cold
 * widget launch builds in ~2.5s of settle time before the app ever renders,
 * but an in-page `window.location.reload()` (exactly what the unpair command
 * does, `App.tsx`) does not, and can race the bridge re-establishing itself.
 *
 * MEASURED 2026-09-08: a re-pair reached through exactly that reload path
 * recorded `model: "Unknown TV"` (the honest `guessModel()` fallback, not a
 * CMS bug) for a panel that reported its real model both before and after -
 * and `model` is written to `Premise.screens[]` once, at redeem, so a
 * transient miss here is not self-correcting the way a wrong heartbeat field
 * would be.
 *
 * A pairing redeem happens once, after a human chooses a room - which takes
 * far longer than this loop ever will - so a few short retries cost nothing
 * on the common path and turn a permanently-wrong stored value into, at
 * worst, one slightly slower pairing. `deviceModel()` itself is unchanged and
 * stays the one-shot call everywhere else (heartbeat, telemetry) already
 * uses it - retrying there would just be retrying the same self-correcting
 * read more often for no benefit.
 */
async function deviceModelWithRetry(): Promise<string> {
  for (let attempt = 0; attempt < MODEL_RETRY_ATTEMPTS; attempt += 1) {
    if (bridge.getDeviceInfo()?.model) break;
    if (attempt < MODEL_RETRY_ATTEMPTS - 1) {
      await new Promise((resolve) => setTimeout(resolve, MODEL_RETRY_DELAY_MS));
    }
  }
  // Whatever the last attempt found - a real model if the bridge settled in
  // time, or the honest `guessModel()` fallback if it genuinely never will.
  return deviceModel();
}

export default function PairingScreen() {
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<Stage>("code");
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState(() => formatClock(new Date()));
  // Why the TV is sitting on this screen (see lib/bootTrace). Crumbs are
  // ALWAYS recorded - they cost nothing, survive reboots, and are the only
  // way a TV in a hotel can explain itself - but they're only rendered on
  // request, so an installer doesn't get developer output on the wall. Add
  // `?diag=1` to the URL (or load the app once with it) to see the trail.
  //
  // ...EXCEPT on a transcluded host, where it is shown unconditionally,
  // because there `?diag=1` is not reachable AT ALL. A Tizen WGT has no URL
  // bar and no address to edit: the app's query comes from the bootloader
  // (see lib/hostQuery), so asking an operator to "add ?diag=1" is asking
  // them to rebuild and re-sign a widget. A diagnostic you cannot reach on
  // the platform that needs it most is not a diagnostic.
  //
  // The original concern still holds everywhere it applies - a Philips box in
  // a hotel shows nothing extra - and the trail itself names no secret: it is
  // reasons and timestamps ("heartbeat_401", "unpair_event", "no_binding").
  const [bootTrace] = useState(() =>
    hostParams().get("diag") === "1" || isTranscludedHost()
      ? formatBootTrace()
      : ""
  );
  // Track most-recent code we tried so the keypad listener doesn't fight
  // submitCode() running asynchronously.
  const lastSubmitRef = useRef("");

  // D-pad focus for TV remotes (no touch, frequently no number keys). The
  // keypad is a 3-col grid: indices 0..8 = digits 1-9, 9 = Clear, 10 = 0,
  // 11 = Backspace, 12 = Continue. Arrow keys move the highlight, Enter/OK
  // presses the focused key. Touch + number-key entry keep working alongside.
  const [keyFocus, setKeyFocus] = useState(0);
  const keyFocusRef = useRef(0);
  // Room stage: rooms laid out as a WRAPPING GRID (see `.pair__rooms`), then
  // "use a different code" as the last index. The column count comes from the
  // live layout rather than a constant, because `.pair__rooms` uses
  // `auto-fill` - a 3-room premise is one row, a 40-room premise is many.
  const [roomFocus, setRoomFocus] = useState(0);
  const roomFocusRef = useRef(0);
  const roomRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const backRef = useRef<HTMLButtonElement | null>(null);
  /** Single-flight latch for the redeem call - see `pickRoom`. */
  const claimingRef = useRef(false);
  // Latest code, readable from the keydown closure without re-binding.
  const codeRef = useRef("");
  useEffect(() => {
    codeRef.current = code;
  }, [code]);

  // Tick the bottom-bar clock every 30s.
  useEffect(() => {
    const t = window.setInterval(() => {
      setClock(formatClock(new Date()));
    }, 30_000);
    return () => window.clearInterval(t);
  }, []);

  // Auto-dismiss the toast after 2.4s.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(t);
  }, [toast]);

  // Keyboard + D-pad for the code stage.
  useEffect(() => {
    if (stage !== "code") return;
    setKeyFocus(0);
    keyFocusRef.current = 0;

    function move(next: number) {
      keyFocusRef.current = next;
      setKeyFocus(next);
    }
    function activate(i: number) {
      if (i >= 0 && i <= 8) pushDigit(String(i + 1));
      else if (i === 9) clearAll();
      else if (i === 10) pushDigit("0");
      else if (i === 11) backspace();
      else if (i === 12) submitIfComplete();
    }
    function onKey(e: KeyboardEvent) {
      const k = e.key;
      // Direct digit entry (number remotes / keyboards).
      if (k >= "0" && k <= "9") {
        e.preventDefault();
        setCode((c) => (c.length < CODE_LEN ? c + k : c));
        return;
      }
      if (k === "Backspace") {
        e.preventDefault();
        setCode((c) => c.slice(0, -1));
        return;
      }
      if (k === "Escape") {
        e.preventDefault();
        setCode("");
        return;
      }
      // D-pad navigation across the keypad grid.
      const i = keyFocusRef.current;
      const col = i < 12 ? i % 3 : 1;
      const row = i < 12 ? Math.floor(i / 3) : 4;
      if (k === "ArrowLeft") {
        e.preventDefault();
        if (i < 12 && col > 0) move(i - 1);
      } else if (k === "ArrowRight") {
        e.preventDefault();
        if (i < 12 && col < 2) move(i + 1);
      } else if (k === "ArrowUp") {
        e.preventDefault();
        if (i === 12) move(10);
        else if (row > 0) move(i - 3);
      } else if (k === "ArrowDown") {
        e.preventDefault();
        if (i < 12 && row < 3) move(i + 3);
        else if (i < 12 && row === 3) move(12);
      } else if (k === "Enter") {
        e.preventDefault();
        activate(keyFocusRef.current);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // D-pad for the room stage.
  //
  // Every arrow key is handled AND preventDefault-ed. That is load-bearing on
  // Android TV/STB webviews: they run their own spatial navigation, so any
  // arrow we leave unhandled moves the native focus ring while our brass
  // highlight stays put. The two then disagree about which room is selected -
  // and since Enter acts on our index, the guest can pair the screen to a
  // different room than the one the ring is on. The roving tabindex on the
  // tiles (see the render below) is the other half of that guarantee.
  useEffect(() => {
    if (stage !== "room" || !preview) return;
    setRoomFocus(0);
    roomFocusRef.current = 0;
    const len = preview.rooms.length;
    const backIdx = len;

    /** Columns per row, read off the live grid (`auto-fill` - not a constant). */
    function cols(): number {
      const els = roomRefs.current.slice(0, len).filter(Boolean);
      if (els.length < 2) return 1;
      const top = els[0]!.offsetTop;
      let n = 0;
      for (const el of els) {
        if (el!.offsetTop !== top) break;
        n++;
      }
      return Math.max(1, n);
    }

    function move(next: number) {
      const clamped = Math.max(0, Math.min(backIdx, next));
      roomFocusRef.current = clamped;
      setRoomFocus(clamped);
    }
    function onKey(e: KeyboardEvent) {
      const k = e.key;
      const i = roomFocusRef.current;
      const c = cols();
      if (k === "ArrowRight") {
        e.preventDefault();
        if (i < len - 1) move(i + 1);
      } else if (k === "ArrowLeft") {
        e.preventDefault();
        if (i > 0 && i < len) move(i - 1);
      } else if (k === "ArrowDown") {
        e.preventDefault();
        // Down from the last grid row lands on "use a different code".
        if (i < len) move(i + c < len ? i + c : backIdx);
      } else if (k === "ArrowUp") {
        e.preventDefault();
        if (i === backIdx) move(len - 1);
        else if (i - c >= 0) move(i - c);
      } else if (k === "Enter") {
        e.preventDefault();
        if (i < backIdx) {
          const r = preview!.rooms[i];
          if (!r.taken) void pickRoom(r.id);
        } else {
          backToCode();
        }
      } else if (k === "Escape" || k === "Backspace") {
        e.preventDefault();
        backToCode();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, preview]);

  // Keep the real DOM focus on the highlighted tile, so the webview's native
  // focus ring and our brass highlight are always the same element - and so a
  // long room list scrolls the selection into view.
  useEffect(() => {
    if (stage !== "room" || !preview) return;
    const el =
      roomFocus >= preview.rooms.length
        ? backRef.current
        : roomRefs.current[roomFocus];
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: "nearest" });
  }, [roomFocus, stage, preview]);

  async function submitCodeNow(value: string) {
    setBusy(true);
    try {
      const res = await fetch(`/api/foyer/pairings/${value}/preview`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        // Lets the server un-block a room THIS device already occupies (a
        // re-pair after a self-unpair) instead of showing it as taken and
        // refusing selection - see PreviewBody's doc comment in pairings.ts.
        body: JSON.stringify({ screenId: getScreenId() }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setToast(
          body.error === "expired"
            ? "That code has expired."
            : body.error === "already_used"
              ? "That code has already been used."
              : "Invalid code - please retry."
        );
        setCode("");
        return;
      }
      setPreview((await res.json()) as PreviewResponse);
      setStage("room");
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Network error.");
    } finally {
      setBusy(false);
    }
  }

  async function pickRoom(roomId: string) {
    if (!preview) return;
    // Single-flight latch. `busy` is React state, so it is still false when a
    // second call arrives in the SAME event-loop turn - which is exactly what
    // happens on an Android STB: OK arrives as a keydown (handled here) and the
    // webview ALSO synthesizes a click on the focused tile. Two redeems each
    // mint a fresh screenToken and overwrite `screens[]`, so the token the TV
    // keeps and the one the server stores can differ - the TV then 401s on its
    // first heartbeat, clears the binding and bounces back to the code screen,
    // while the CMS still shows the device paired. Deliberately NOT released on
    // success: we navigate away, and a room may only be claimed once.
    if (claimingRef.current) return;
    claimingRef.current = true;
    let claimed = false;
    setBusy(true);
    try {
      const model = await deviceModelWithRetry();
      const res = await fetch(`/api/foyer/pairings/${code}/redeem`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          screenId: getScreenId(),
          model,
          roomId,
          // Stored server-side so this TV can re-attach by serial after a
          // clean-state wipe (native Philips shell only; undefined elsewhere).
          serial: bridge.getDeviceInfo()?.serial,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setToast(humanizeRedeemError(body.error, res.status, res.statusText));
        return;
      }
      const body = (await res.json()) as { screenToken?: string };
      if (!body.screenToken) {
        setToast("Pairing succeeded but no screen token was issued.");
        return;
      }
      setBinding({
        premiseId: preview.premise.id,
        roomId,
        screenToken: body.screenToken,
      });
      claimed = true;
      // Prove the write actually landed in the durable store BEFORE we
      // navigate - if the next boot reports no_binding despite this crumb,
      // the store lost it across the reload rather than never taking it.
      noteBoot("paired", getBinding() ? "stored" : "write_failed");
      // App.tsx re-reads the binding from localStorage on re-mount.
      restartApp();
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Network error.");
    } finally {
      // Only a completed claim keeps the latch closed. Every failure path
      // (bad code, taken room, no free seats, network blip) must re-open it
      // so the guest can retry.
      if (!claimed) claimingRef.current = false;
      setBusy(false);
    }
  }

  function pushDigit(d: string) {
    setCode((c) => (c.length < CODE_LEN ? c + d : c));
  }
  function backspace() {
    setCode((c) => c.slice(0, -1));
  }
  function clearAll() {
    setCode("");
  }
  function submitIfComplete() {
    if (
      codeRef.current.length === CODE_LEN &&
      codeRef.current !== lastSubmitRef.current
    ) {
      lastSubmitRef.current = codeRef.current;
      void submitCodeNow(codeRef.current);
    }
  }
  function backToCode() {
    setStage("code");
    setPreview(null);
    setCode("");
    lastSubmitRef.current = "";
  }

  // Auto-submit once the user fills all six digits.
  useEffect(() => {
    if (stage !== "code") return;
    if (code.length !== CODE_LEN) return;
    if (code === lastSubmitRef.current) return;
    lastSubmitRef.current = code;
    void submitCodeNow(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, stage]);

  const firstEmptyIdx = (() => {
    const i = Array.from({ length: CODE_LEN }, (_, n) => n).find(
      (n) => !code[n]
    );
    return i === undefined ? CODE_LEN : i;
  })();
  const screenIdShort = getScreenId().replace(/^scr-/, "").slice(0, 6);
  const kf = (i: number) => (keyFocus === i ? " pair__key--focus" : "");

  return (
    <div className="pair">
      {/* TOP */}
      <div className="pair__topbar">
        <div className="pair__brand">
          <span className="pair__brand-mark" aria-hidden="true" />
          <span>Foyer</span>
        </div>
        <div className="pair__device-chip" data-testid="pair-device-chip">
          <span className="dot" />
          <span>{`Online · ${deviceModel()} · ${screenIdShort}`}</span>
        </div>
      </div>

      {/* MAIN */}
      <div className="pair__stage">
        {stage === "code" && (
          <>
            <div className="pair__left">
              <div className="pair__eyebrow">Pair this device</div>
              <h1 className="pair__headline">
                Enter the <em>six‑digit code</em> to pair.
              </h1>
              <p className="pair__lede">
                Enter the code given from your installation partner using the
                keypad on this screen.
              </p>

              <div
                className="pair__code"
                data-testid="pair-pin"
                aria-label="Pairing code"
              >
                {Array.from({ length: CODE_LEN }).map((_, i) => {
                  const isFilled = !!code[i];
                  const isActive =
                    i === firstEmptyIdx && code.length < CODE_LEN;
                  const cls = [
                    "pair__slot",
                    isFilled && "pair__slot--filled",
                    isActive && "pair__slot--active",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <div key={i} className={cls} data-testid={`pair-slot-${i}`}>
                      {code[i] ?? ""}
                    </div>
                  );
                })}
              </div>

              <p className="pair__hint">
                Codes refresh. If yours has expired, contact your partner to
                retrieve a new one.
              </p>
            </div>

            <div className="pair__right">
              <div className="pair__keypad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button
                    key={n}
                    className={`pair__key${kf(n - 1)}`}
                    onClick={() => pushDigit(String(n))}
                    data-testid={`pair-key-${n}`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  className={`pair__key pair__key--muted${kf(9)}`}
                  onClick={clearAll}
                  data-testid="pair-clear"
                >
                  Clear
                </button>
                <button
                  className={`pair__key${kf(10)}`}
                  onClick={() => pushDigit("0")}
                  data-testid="pair-key-0"
                >
                  0
                </button>
                <button
                  className={`pair__key pair__key--muted${kf(11)}`}
                  onClick={backspace}
                  aria-label="Backspace"
                  data-testid="pair-back"
                >
                  {backspaceIcon()}
                </button>
              </div>

              <div className="pair__cta-row">
                <button
                  className={`pair__continue${keyFocus === 12 ? " pair__continue--focus" : ""}`}
                  disabled={code.length !== CODE_LEN || busy}
                  onClick={() => submitIfComplete()}
                  data-testid="pair-submit"
                >
                  Continue
                  {arrowRightIcon()}
                </button>
              </div>
            </div>
          </>
        )}

        {stage === "room" && preview && (
          <>
            <div className="pair__left">
              <div className="pair__eyebrow">Pick this device&rsquo;s room</div>
              <h1 className="pair__headline">
                Connect to <em>{preview.premise.name}</em>.
              </h1>
              <p className="pair__lede">
                Choose the room this screen will live in. We&rsquo;ll bind the
                device to that room and load its content.
              </p>

              <button
                ref={backRef}
                className={`pair__back${roomFocus === preview.rooms.length ? " pair__back--focus" : ""}`}
                onClick={backToCode}
                tabIndex={roomFocus === preview.rooms.length ? 0 : -1}
                data-testid="pair-back-to-code"
              >
                ← Use a different code
              </button>
            </div>

            <div className="pair__right">
              <div className="pair__rooms" data-testid="pair-rooms">
                {preview.rooms.map((r, idx) => (
                  <button
                    key={r.id}
                    ref={(el) => {
                      roomRefs.current[idx] = el;
                    }}
                    className={`pair__room${roomFocus === idx ? " pair__room--focus" : ""}${r.taken ? " pair__room--taken" : ""}`}
                    onClick={() => {
                      if (!r.taken && !busy) pickRoom(r.id);
                    }}
                    /* Roving tabindex: exactly one tile is focusable at a time,
                       so the webview's spatial navigation cannot move the focus
                       ring off our highlighted tile. */
                    tabIndex={roomFocus === idx ? 0 : -1}
                    /* Deliberately aria-disabled rather than `disabled`: a taken
                       room must stay focusable so the D-pad can land on it and
                       show the "unavailable" state. `disabled` would make it
                       unfocusable, desyncing the ring from our highlight, and on
                       some Android webviews would also grey out the label. */
                    aria-disabled={r.taken || busy}
                    data-testid={`pair-room-${r.id}`}
                  >
                    <div className="pair__room-id">{r.id}</div>
                    {r.name && <div className="pair__room-name">{r.name}</div>}
                    {r.taken && <div className="pair__room-tag">paired</div>}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* BOTTOM */}
      <div className="pair__bottombar">
        <div className="pair__meta">
          <span>
            Build<strong>v{APP_VERSION}</strong>
          </span>
          <span>
            Region<strong>EU‑North</strong>
          </span>
          <span>
            Time<strong>{clock}</strong>
          </span>
          {/* Why this screen is showing. Empty on a first-ever install; on a TV
              that bounced back here it names the path it took (no_binding /
              heartbeat_401 / unpair_event / reset_flag). */}
          {bootTrace && (
            <span className="pair__trace" data-testid="pair-boot-trace">
              Trace<strong>{bootTrace}</strong>
            </span>
          )}
        </div>
        <div className="pair__help">
          <kbd>?</kbd>
          <span>
            Need help - contact support@nenda.com or visit foyer.nenda.com from
            a computer
          </span>
        </div>
      </div>

      <div
        className={`pair__toast${toast ? " pair__toast--show" : ""}`}
        role="status"
        data-testid="pair-toast"
      >
        {toast ?? ""}
      </div>
    </div>
  );
}

function formatClock(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * Map the server's machine-readable error codes from /redeem to copy a
 * hotel installer can act on. Unknown codes fall back to the raw HTTP
 * line so we don't swallow new errors silently.
 */
function humanizeRedeemError(
  code: string | undefined,
  status: number,
  statusText: string
): string {
  switch (code) {
    case "no_free_licenses":
      return "No license seats available for this premise. Contact your installer.";
    case "room_already_taken":
      return "Another TV is already paired to this room.";
    case "unknown_room":
      return "That room isn't part of this premise.";
    case "missing_fields":
      return "Missing fields in pairing request.";
    case "expired":
      return "That code has expired.";
    case "invalid_code":
      return "That code is no longer valid.";
    case "premise_missing":
      return "Premise not found.";
    default:
      return code ?? `${status} ${statusText}`;
  }
}
